'use client';

import React from 'react';
import { ArrowRight, Phone, Car, Sparkles } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { safeHref } from '@/lib/storefront/safe-url';
import { containerClass, sectionStyle } from './HeaderSections';

// ──────────────────────────────────────────────────────────────
// 1. Centered banner CTA
// ──────────────────────────────────────────────────────────────

export function CtaBanner({ content, style }: SectionProps) {
  const {
    headline = 'Ready to hit the road?',
    subheadline = 'Book today and pick up tomorrow. Free cancellation up to 24 hours before your rental.',
    ctaText = 'Book a vehicle',
    ctaHref = '/vehicles',
    secondaryCtaText = 'Call us',
    secondaryCtaHref = 'tel:+212528840000',
  } = content;

  return (
    <section className="w-full bg-slate-900" style={sectionStyle(style)}>
      <div className={`${containerClass(style)} py-4 text-center`}>
        <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">{headline}</h2>
        {subheadline && <p className="mx-auto mt-3 max-w-xl text-base text-white/70">{subheadline}</p>}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href={safeHref(ctaHref)} className="inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90">
            {ctaText}
            <ArrowRight className="h-4 w-4" />
          </a>
          {secondaryCtaText && (
            <a href={safeHref(secondaryCtaHref)} className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10">
              <Phone className="h-4 w-4" />
              {secondaryCtaText}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 2. Split CTA with image
// ──────────────────────────────────────────────────────────────

export function CtaSplit({ content, style }: SectionProps) {
  const {
    eyebrow = 'Limited availability',
    headline = 'Summer rates are live',
    subheadline = 'Lock in your dates now — prices go up in July. Free changes until 48 hours before pick-up.',
    ctaText = 'See summer deals',
    ctaHref = '/deals',
    imageUrl = 'https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=1400&q=80',
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="grid overflow-hidden rounded-3xl border border-slate-200 lg:grid-cols-2">
          <div className="order-2 p-8 sm:p-12 lg:order-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">{headline}</h2>
            <p className="mt-3 text-base text-slate-600">{subheadline}</p>
            <a href={safeHref(ctaHref)} className="mt-7 inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90">
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="order-1 min-h-[240px] lg:order-2">
            <img src={imageUrl} alt="" className="h-full w-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 3. Two-action card CTA
// ──────────────────────────────────────────────────────────────

export function CtaCards({ content, style }: SectionProps) {
  const {
    headline = 'Two ways to get on the road',
    subheadline = 'Book online in minutes, or talk to the team if you need something specific.',
    cards = [
      {
        icon: 'car',
        title: 'Book online',
        text: 'Choose your car, pick your dates and pay securely. Instant confirmation.',
        ctaText: 'Browse the fleet',
        ctaHref: '/vehicles',
      },
      {
        icon: 'phone',
        title: 'Talk to us',
        text: 'Long-term rental, corporate account or a special request? We answer 7 days a week.',
        ctaText: 'Call the office',
        ctaHref: 'tel:+212528840000',
      },
    ],
  } = content;

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className="mx-auto mt-10 grid max-w-4xl gap-5 sm:grid-cols-2">
          {cards.map((card: any, i: number) => {
            const Icon = card.icon === 'phone' ? Phone : card.icon === 'sparkles' ? Sparkles : Car;
            return (
              <div key={i} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{card.title}</h3>
                <p className="mt-2 flex-1 text-sm text-slate-600">{card.text}</p>
                <a href={card.ctaHref} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:gap-2.5 transition-all">
                  {card.ctaText}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export const CTA_VARIATIONS = {
  banner: CtaBanner,
  split: CtaSplit,
  cards: CtaCards,
} as const;

export type CtaVariationKey = keyof typeof CTA_VARIATIONS;
